import React, { useState, useEffect, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Input, DatePicker, Button, Badge, Select, Modal, Drawer } from 'antd';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from 'controllers/notificationController';
import pdfMake from "pdfmake/build/pdfmake";
import * as XLSX from 'xlsx';
import pdfFonts from "pdfmake/build/vfs_fonts";
import moment from "moment-timezone";
import vijayDairyLogo from "../../../assets/images/vijay-dairy-logo.png";

import { getAllDeliveriesAction, getAllInvoicesAction, updateDeliveryStatusAction } from '@app/store/slices/userSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import styled from 'styled-components';
import { BaseSpace } from '@app/components/common/BaseSpace/BaseSpace';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useNavigate, useParams } from 'react-router-dom';
import { DownloadOutlined, EyeOutlined, PrinterOutlined } from '@ant-design/icons';
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { fontStyle } from 'html2canvas/dist/types/css/property-descriptors/font-style';

const { RangePicker } = DatePicker;
const { Option } = Select;

export const BasicTable: React.FC = () => {
  const [tableKey, setTableKey] = useState(0);
  const [filters, setFilters] = useState({
    name: '',
    createdAt: [],
    deliveryStatus: '',
    phone: '',
    order_type: '',
    orderId: '',
    invoiceNumber: ''
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [tableData, setTableData] = useState({ data: [], loading: false, total: 0 });

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(false);
  const [invoiceFilter, setInvoiceFilter] = useState('');
  const [allData, setAllData] = useState([]);
  const [isFetchingAll, setIsFetchingAll] = useState(false);
  const dispatch = useAppDispatch();
  let navigate = useNavigate();
  const { id } = useParams();
  // Fetch data with updated filters and pagination
 const fetchData = useCallback(() => {
    setTableData((prev) => ({ ...prev, loading: true }));

    const params = {
      ...filters,
      createdAt: filters.createdAt.length
        ? [
          moment(filters.createdAt[0]).toISOString(),
          moment(filters.createdAt[1]).toISOString(),
        ]
        : [],
      current: pagination.current,
      limit: pagination.pageSize,
    };

    dispatch(getAllInvoicesAction(params)).unwrap()
      .then((response) => {
        // ✅ Backend se globalStartIndex lo
        const startInvoiceNumber = 100100 + (
          response.globalStartIndex ?? ((pagination.current - 1) * pagination.pageSize)
        );

        const dataWithInvoiceNumber = response.data.map((item: any, index: number) => ({
          ...item,
          invoiceNumber: startInvoiceNumber + index,
        }));

        setTableData({
          data: dataWithInvoiceNumber,
          loading: false,
          total: response.total,
        });
      })
      .catch((error) => {
        notificationController.error({ message: error.message || 'Failed to fetch data' });
        setTableData((prev) => ({ ...prev, loading: false }));
      });
  }, [pagination, filters, dispatch]);

  const generateExcelExport = () => {
    try {
      const dataToExport = filteredData;

      if (!dataToExport || dataToExport.length === 0) {
        notificationController.error({
          message: 'No data to export',
        });
        return;
      }

      const rows = dataToExport.map((record: any) => ({
        'Invoice No.': record.invoiceNumber ?? 'Not available',
        'Order ID': record.orderId || 'Not available',
        'Customer Name': record?.client_id?.name || 'Not available',
        'Phone': record?.client_id?.phone || 'Not available',
        'Products': record?.products
          ?.map((x: any) =>
            x.option_id
              ? `${x.name} (${x.cart_quantity})`
              : `${x.name} -- (${x.cart_quantity} x ${x.quantity} ${x.unit})`
          )
          .join(', ') || 'Not available',
        'Amount (Rs)': record?.amount ?? 0,
        'Wallet Balance (Rs)': record?.client_id?.balance ?? 0,
        'Invoice Date': record.createdAt
          ? moment(record.createdAt).tz('Asia/Kolkata').format('DD-MMM-YYYY')
          : '',
        'Status': record?.status || '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);

      worksheet['!cols'] = [
        { wch: 14 },
        { wch: 20 },
        { wch: 22 },
        { wch: 14 },
        { wch: 40 },
        { wch: 14 },
        { wch: 18 },
        { wch: 16 },
        { wch: 12 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices');

      const fileName = `Invoices_Page${pagination.current}_${moment().format('DDMMMYYYY_HHmm')}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      // ✅ SUCCESS MESSAGE
      notificationController.success({
        message: 'Excel exported successfully!',
      });

    } catch (error) {
      console.error('Export Error:', error);

      // ❌ ERROR MESSAGE
      notificationController.error({
        message: 'Failed to export Excel. Please try again!',
      });
    }
  };
  const fetchAllDataForInvoiceFilter = useCallback((searchValue: string) => {
    if (!searchValue) {
      setInvoiceFilter('');
      setAllData([]);
      return;
    }

    const searchNumber = parseInt(searchValue.trim());

    // ✅ Global index nikalo
    const globalIndex = searchNumber - 100100;

    // Invalid number — exist nahi kar sakta
    if (isNaN(globalIndex) || globalIndex < 0) {
      setAllData([]);
      setInvoiceFilter(searchValue);
      return;
    }

    // Kis page pe hoga yeh record?
    const targetPage = Math.floor(globalIndex / pagination.pageSize) + 1;
    const targetPageSize = pagination.pageSize;

    setIsFetchingAll(true);

    const params = {
      ...filters,
      createdAt: filters.createdAt.length
        ? [
          moment(filters.createdAt[0]).toISOString(),
          moment(filters.createdAt[1]).toISOString(),
        ]
        : [],
      current: targetPage,      // ✅ directly usi page pe jao
      pageSize: targetPageSize,
    };

    dispatch(getAllInvoicesAction(params)).unwrap()
      .then((response) => {
        const startInvoiceNumber = 100100 + ((targetPage - 1) * targetPageSize);
        const dataWithInvoiceNumber = response.data.map((item: any, index: number) => ({
          ...item,
          invoiceNumber: startInvoiceNumber + index,
        }));

        // Filter karo exact match
        const matched = dataWithInvoiceNumber.filter((item: any) =>
          String(item.invoiceNumber).includes(searchValue.trim())
        );

        setAllData(matched);
        setInvoiceFilter(searchValue);

        // ✅ Us page pe pagination set karo
        setPagination({ current: targetPage, pageSize: targetPageSize });

        setIsFetchingAll(false);
      })
      .catch((error) => {
        notificationController.error({ message: error.message || 'Failed to fetch data' });
        setIsFetchingAll(false);
      });
  }, [filters, dispatch, pagination.pageSize]);

  // Trigger fetch when filters or pagination change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    function hideError(e) {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    }

    window.addEventListener('error', hideError)
    return () => {
      window.addEventListener('error', hideError)
    }
  }, [])

  // Trigger fetch when filters or pagination change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange = (filterName: string, value: any) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  // Handle table pagination change
  const handleTableChange = (paginationInfo) => {
    setPagination({ current: paginationInfo.current, pageSize: paginationInfo.pageSize });
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Invoice No.',
      dataIndex: 'invoiceNumber',
      render: (text, record) => (
        <span>{record.invoiceNumber ?? 'Not available'}</span>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Invoice No."
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
            }}
            onPressEnter={() => {
              confirm();
              fetchAllDataForInvoiceFilter(selectedKeys[0] as string);
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => {
              confirm();
              fetchAllDataForInvoiceFilter(selectedKeys[0] as string);
            }}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              setInvoiceFilter(''); // ✅ filter clear
              setAllData([]);   // ✅ all data clear
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      render: (text) => <span>{text || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Order ID"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('orderId', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    // {
    //   title: 'Subscription ID',
    //   dataIndex: 'subscription_id',
    //   render: (text) => <span>{text || 'Not available'}</span>,
    //   filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    //     <div style={{ padding: 8 }}>
    //       <Input
    //         placeholder="Search Subscription ID"
    //         value={selectedKeys[0]}
    //         onChange={(e) => {
    //           const value = e.target.value;
    //           setSelectedKeys(value ? [value] : []);
    //           handleFilterChange('subscription_id', value); // Update the filter state
    //         }}
    //         onPressEnter={() => confirm()}
    //         style={{ width: 188, marginBottom: 8, display: 'block' }}
    //       />
    //       <Button
    //         type="primary"
    //         onClick={() => confirm()}
    //         size="small"
    //         style={{ width: 90, marginRight: 8 }}
    //       >
    //         Search
    //       </Button>
    //       <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
    //         Reset
    //       </Button>
    //     </div>
    //   ),
    // },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (text, record) => <span>{record?.client_id?.name || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Name"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('name', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      // onFilter: (value, record) => record?.client?.name.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      render: (text, record) => <span>{record?.client_id?.phone || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Phone"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('phone', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),

    },
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      render: (text, record) =>

        record?.products?.map((x, idx) => (
          <span key={idx}>
            {x.name} {!x.option_id && `-- (${x.quantity} ${x.unit})`}
            {idx < record.products.length - 1 && ', '}
          </span>
        )),


      // onFilter: (value, record) => record?.product?.name.toLowerCase().includes(value.toLowerCase()),
    },

    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (text, record) => <span>Rs {record?.amount}</span>,
    },
    {
      title: 'Wallet Balance',
      dataIndex: 'balance',
      render: (text, record) => {
        const walletBalance = record?.client_id?.balance ?? 0;
        const amount = record?.amount ?? 0;

        return (
          <span style={{ color: walletBalance < amount ? 'red' : 'black' }}>
            Rs {walletBalance}
          </span>
        );
      },
    },
    {
      title: 'Invoice Date',
      dataIndex: 'createdAt',
      render: (text, record) => (
        <span>
          {record.createdAt ? moment(record?.createdAt).tz("Asia/Kolkata").format("DD-MMM-YYYY") : ""}
        </span>
      ),

      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <RangePicker
            value={selectedKeys[0] ? selectedKeys : []}
            onChange={(dates) => {
              setSelectedKeys(dates || []);
              handleFilterChange('createdAt', dates || []); // Update the filter state
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (text, record) => <span>{text}</span>,
      // filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      //   <div style={{ padding: 8 }}>
      //     <Select
      //       value={selectedKeys[0] || ''}
      //       onChange={(value) => {
      //         setSelectedKeys(value ? [value] : []);
      //         handleFilterChange('deliveryStatus', value); // Update the filter state
      //       }}
      //       style={{ width: 188, marginBottom: 8 }}
      //       placeholder="Select Status"
      //     >
      //       <Option value="Scheduled">Scheduled</Option>
      //       <Option value="Delivered">Delivered</Option>
      //       <Option value="Pending">Pending</Option>
      //       <Option value="Skipped">Skipped</Option>
      //     </Select>
      //     <Button
      //       type="primary"
      //       onClick={() => confirm()}
      //       size="small"
      //       style={{ width: 90, marginRight: 8, marginTop: '10px' }}
      //     >
      //       Search
      //     </Button>
      //     <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
      //       Reset
      //     </Button>
      //   </div>
      // ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewInvoice(record)}
        >
          View
        </Button>
      ),
    },

  ];

  const filteredData = invoiceFilter && allData.length > 0
    ? allData
    : invoiceFilter && allData.length === 0
      ? []
      : tableData.data;
  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setDrawerVisible(true);
  };

  const handlePrint = () => {
    window.print();
  };

  if (pdfMake && pdfFonts && pdfFonts.pdfMake) {
    pdfMake.vfs = pdfFonts.pdfMake.vfs;
  }


  const generateInvoicePDF = () => {
    if (!selectedInvoice) return;

    const formattedDate = selectedInvoice.createdAt
      ? moment(selectedInvoice.createdAt).tz("Asia/Kolkata").format("DD-MMM-YYYY")
      : "";

    const productRows = selectedInvoice?.products?.map((x) => {
      const rate = x.price || 0;
      const quantity = x.quantity || 0;
      const cartQuantity = x.cart_quantity || 0;
      const tax = x.tax || 0;
      const amt = cartQuantity * rate + (cartQuantity * rate * tax) / 100;
      return [
        { text: x.name || "", style: "tableCell" },
        {
          text: x.option_id
            ? `${cartQuantity}`
            : `${cartQuantity} x ${quantity} ${x.unit || ""}`,
          style: "tableCellCenter",
        },
        { text: `₹ ${rate}`, style: "tableCellCenter" },
        { text: `${tax}%`, style: "tableCellCenter" },
        { text: `₹ ${amt.toFixed(0)}`, style: "tableCellRight" },
      ];
    }) || [];

    const itemTotal = selectedInvoice?.amount || 0;
    const total = itemTotal;

    const clientTotalBalance = selectedInvoice?.client_id?.balance || 0;
    const walletBalanceBefore = clientTotalBalance;
    const walletBalanceAfter = clientTotalBalance - total;

    const client = selectedInvoice?.client_id;

    // Tax Summary calculations
    const taxSummary: Record<string, { taxable: number; taxAmt: number }> = {};
    selectedInvoice?.products?.forEach((x) => {
      const rate = x.price || 0;
      const cartQuantity = x.cart_quantity || 0;
      const tax = x.tax || 0;
      const taxable = cartQuantity * rate;
      const taxAmt = (taxable * tax) / 100;
      const key = `${tax}`;
      if (!taxSummary[key]) taxSummary[key] = { taxable: 0, taxAmt: 0 };
      taxSummary[key].taxable += taxable;
      taxSummary[key].taxAmt += taxAmt;
    });

    const totalTaxableAmt = Object.values(taxSummary).reduce((s, v) => s + v.taxable, 0);
    const totalTaxAmt = Object.values(taxSummary).reduce((s, v) => s + v.taxAmt, 0);

    const taxDetailRows = Object.entries(taxSummary).map(([rate, val]) => [
      { text: `GST (${rate}%):`, style: "taxRowLabel" },
      { text: `₹ ${val.taxAmt.toFixed(0)}`, style: "taxRowValue" },
    ]);

    const docDefinition: any = {
      pageSize: "A4",
      pageMargins: [40, 40, 40, 80],

      footer: (currentPage: number, pageCount: number) => ({
        stack: [
          {
            canvas: [
              {
                type: "line",
                x1: 40, y1: 0, x2: 555, y2: 0,
                lineWidth: 0.8,
                lineColor: "#cccccc",
              },
            ],
            margin: [0, 0, 0, 6],
          },
          {
            columns: [
              {
                text: "Thank you for your business!\nFresh daily delivered with care.",
                style: "footerText",
                width: "60%",
                margin: [40, 0, 0, 0],
              },
              {
                stack: [
                  { text: "Vijay Dairy", style: "footerBrand" },
                  { text: "This is a computer-generated invoice.", style: "footerSub" },
                ],
                alignment: "right",
                width: "40%",
                margin: [0, 0, 40, 0],
              },
            ],
          },
        ],
      }),

      content: [
        // ─── HEADER ──────────────────────────────────────────────────
        {
          columns: [
            {
              image: vijayDairyLogo,
              width: 90,
              height: 40,
            },
            {
              width: "*",
              text: "",
            },
            {
              stack: [
                { text: "INVOICE", style: "invoiceHeading" },
                { text: `#${selectedInvoice?.invoiceNumber || ""}`, style: "invoiceNumber" },
              ],
              alignment: "right",
            },
          ],
          margin: [0, 0, 0, 10],
        },

        // ─── HORIZONTAL RULE ─────────────────────────────────────────
        {
          canvas: [
            { type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: "#cccccc" },
          ],
          margin: [0, 0, 0, 14],
        },

        // ─── BILL TO + STATUS ─────────────────────────────────────────
        {
          columns: [
            {
              stack: [
                { text: "Bill To:", style: "billToLabel" },
                { text: client?.name || "", style: "clientName" },
                {
                  text: [
                    { text: "Phone No: ", style: "billToLabel" },
                    { text: `${client?.phone || "-"}`, style: "clientDetail" },
                  ],
                  margin: [0, 2, 0, 0],
                },
                {
                  text: [
                    { text: "Address: ", style: "billToLabel" },
                    { text: `${client?.city || "-"}, ${client?.state || "-"}`, style: "clientDetail" },
                  ],
                  margin: [0, 2, 0, 0],
                },
                {
                  text: [
                    { text: "Pin Code: ", style: "billToLabel" },
                    { text: `${client?.pincode || "-"}`, style: "clientDetail" },
                  ],
                  margin: [0, 2, 0, 0],
                },
              ],
              width: "75%",
            },
            {
              stack: [
                {
                  table: {
                    widths: ["auto", "auto"],
                    body: [
                      [
                        { text: "Invoice Date:", style: "metaLabel", alignment: "right" },
                        { text: formattedDate || "N/A", style: "metaValueBold", alignment: "right" },
                      ],
                      [
                        { text: "Status:", style: "metaLabel", alignment: "right" },
                        { text: selectedInvoice?.status || "Delivered", style: "metaValueBold", alignment: "right" },
                      ],
                    ],
                  },
                  layout: "noBorders",
                  alignment: "right",
                },
              ],
              width: "26%",
              alignment: "right",
            },
          ],
          margin: [0, 0, 0, 0],
        },

        // ─── HORIZONTAL RULE ─────────────────────────────────────────
        {
          canvas: [
            { type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: "#cccccc" },
          ],
          margin: [0, 10, 0, 14],
        },

        // ─── ITEMS SECTION TITLE ──────────────────────────────────────
        { text: "ITEMS", style: "sectionTitle", margin: [0, 0, 0, 6] },

        // ─── PRODUCTS TABLE ───────────────────────────────────────────
        {
          table: {
            headerRows: 1,
            widths: ["*", 90, 60, 45, 70],
            body: [
              [
                { text: "ITEM", style: "colHeader" },
                { text: "QTY", style: "colHeaderCenter" },
                { text: "RATE", style: "colHeaderCenter" },
                { text: "TAX", style: "colHeaderCenter" },
                { text: "AMOUNT", style: "colHeaderRight" },
              ],
              ...productRows,
              // ─── SUBTOTAL ROW (table border ke andar) ────────────────
              [
                {
                  text: "Subtotal",
                  style: "subtotalLabel",
                  colSpan: 4,
                  border: [true, true, false, true],
                },
                {}, {}, {},
                {
                  text: `₹ ${itemTotal.toFixed(0)}`,
                  style: "subtotalValue",
                  border: [false, true, true, true],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: function (i, node) {
              // top & bottom border
              if (i === 0 || i === node.table.body.length) {
                return 0.3; // thin outer border
              }
              return 0.3; // andar ki lines bhi same thin
            },

            vLineWidth: function (i, node) {
              // left & right outer border
              if (i === 0 || i === node.table.widths.length) {
                return 0.3; // thin outer border
              }
              return 0; // andar vertical lines remove
            },

            hLineColor: function () {
              return "#d9d9d9"; // light grey (same as UI)
            },

            vLineColor: function () {
              return "#d9d9d9"; // same color for outer border
            },
            fillColor: (i: number) => {
              if (i === 0) return "#2196f3";
              return "#ffffff";
            },
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 8,
            paddingBottom: () => 8,
          },
          margin: [0, 0, 0, 20],
        },

        // ─── BOTTOM SECTION: Tax+Bill LEFT | Wallet RIGHT ─────────────
  {
  columns: [
    // LEFT COLUMN → Wallet Summary
    {
      width: "50%",
      stack: [
        { text: "Wallet Summary", style: "sectionTitleBlue", margin: [4, 0, 0, 6] },
        {
          table: {
            widths: ["*", "auto"],
            body: [
              [
                { text: "Wallet Balance (Before):", style: "walletRowLabel" },
                { text: `₹ ${walletBalanceBefore.toFixed(0)}`, style: "walletRowValue" },
              ],
              [
                { text: "Order Amount:", style: "walletRowLabel" },
                { text: `₹ ${total.toFixed(0)}`, style: "walletRowValue" },
              ],
              [
                {
                  text: "Wallet Balance (After):",
                  style: "walletRowLabelBold",
                  color: Number(walletBalanceAfter) < 0 ? "red" : "black",
                },
                {
                  text: `₹ ${Number(walletBalanceAfter).toFixed(0)}`,
                  style: "walletRowValueBold",
                  color: Number(walletBalanceAfter) < 0 ? "red" : "black",
                },
              ],
            ],
          },
          layout: {
            hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 0.3 : 0.3),
            vLineWidth: (i, node) => (i === 0 || i === node.table.widths.length ? 0.3 : 0),
            hLineColor: () => "#d9d9d9",
            vLineColor: () => "#d9d9d9",
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 6,
            paddingBottom: () => 6,
          },
          margin: [0, 0, 0, 0],
        },
        ...(Number(walletBalanceAfter) < 0
          ? [{ text: "Please update your wallet balance before placing the next order.", color: "red", fontSize: 9, margin: [6, 4, 0, 4] }]
          : [{ text: "", margin: [0, 8, 0, 0] }]),
      ],
    },

    // RIGHT COLUMN → Bill Summary
    {
      width: "50%",
      stack: [
        { text: "Bill Summary", style: "sectionTitleBlue", margin: [4, 0, 0, 6] },
        {
          table: {
            widths: ["*", "auto"],
            body: [
              [
                { text: "Item Total:", style: "billRow" },
                { text: `₹ ${itemTotal.toFixed(0)}`, style: "billRowRight" },
              ],
              [
                { text: "Final Payable:", style: "billRowBold" },
                { text: `₹ ${total.toFixed(0)}`, style: "billRowRightBold" },
              ],
            ],
          },
          layout: {
            hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 0.3 : 0.3),
            vLineWidth: (i, node) => (i === 0 || i === node.table.widths.length ? 0.3 : 0),
            hLineColor: () => "#d9d9d9",
            vLineColor: () => "#d9d9d9",
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 6,
            paddingBottom: () => 6,
          },
          margin: [0, 0, 0, 0],
        },
      ],
    },
  ],
  columnGap: 20,
},
      ],

      styles: {
        invoiceHeading: { fontSize: 16, bold: true, marginBottom: 5 },
        invoiceNumber: { fontSize: 12, color: "gray" },
        invoiceDateLabel: { fontSize: 10, color: "#555555", margin: [0, 4, 0, 0] },
        invoiceDateValue: { fontSize: 13, bold: true, color: "#000000", margin: [0, 2, 0, 0] },
        billToLabel: { fontSize: 10, color: "#555555", margin: [0, 0, 0, 4] },
        clientName: { fontSize: 13, color: "#000000", margin: [0, 0, 0, 2] },
        clientDetail: { fontSize: 10, color: "#000000", margin: [0, 1, 0, 1] },
        metaLabel: { fontSize: 10, color: "#555555" },
        metaValueBold: { fontSize: 11, color: "#000000" },
        sectionTitle: { fontSize: 11, color: "#2196f3", letterSpacing: 1 },
        sectionTitleBlue: { fontSize: 11, color: "#2196f3" },
        sectionTitleDark: { fontSize: 11, bold: true, color: "#000000" },
        colHeader: { fontSize: 9, bold: true, color: "#ffffff", margin: [0, 2, 0, 2] },
        colHeaderCenter: { fontSize: 9, bold: true, color: "#ffffff", alignment: "left", margin: [0, 2, 0, 2] },
        colHeaderRight: { fontSize: 9, bold: true, color: "#ffffff", alignment: "left", margin: [0, 2, 0, 2] },
        tableCell: { fontSize: 10, color: "#000000" },
        tableCellCenter: { fontSize: 10, color: "#000000", alignment: "left" },
        tableCellRight: { fontSize: 10, color: "#000000", alignment: "left", bold: true },
        subtotalLabel: { fontSize: 10, bold: true, color: "#000000" },
        subtotalValue: { fontSize: 10, color: "#000000", bold: true, alignment: "left" },
        subtotalLabel2: { fontSize: 10, color: "#555555" },
        subtotalValue2: { fontSize: 10, color: "#000000", bold: true, alignment: "left" },
        taxRowLabel: { fontSize: 10, color: "#000000" },
        taxRowValue: { fontSize: 10, color: "#000000", alignment: "left" },
        taxTotalLabel: { fontSize: 10, bold: true, color: "#000000" },
        taxTotalValue: { fontSize: 10, bold: true, color: "#000000", alignment: "left" },
        walletHeader: { fontSize: 10, bold: true, color: "#2196f3" },
        walletRowLabel: { fontSize: 10, color: "#000000" },
        walletRowValue: { fontSize: 10, color: "#000000", alignment: "left" },
        walletRowLabelBold: { fontSize: 10, bold: true, color: "#000000" },
        walletRowValueBold: { fontSize: 10, bold: true, color: "#000000", alignment: "left" },
        billRow: { fontSize: 10, color: "#333333" },
        billRowRight: { fontSize: 10, color: "#000000", alignment: "left" },
        billRowBold: { fontSize: 11, bold: true, color: "#000000" },
        billRowRightBold: { fontSize: 11, bold: true, color: "#000000", alignment: "left" },
        totalLabel: { fontSize: 11, bold: true, color: "#ffffff", letterSpacing: 1 },
        totalValue: { fontSize: 14, bold: true, color: "#ffffff", alignment: "left" },
        footerText: { fontSize: 9, color: "#555555", lineHeight: 1.2 },
        footerBrand: { fontSize: 11, bold: true, color: "#000000" },
        footerSub: { fontSize: 9, color: "#777777", margin: [0, 2, 0, 0] },
      },

      defaultStyle: { fontSize: 10, color: "#000000" },
    };

    pdfMake.createPdf(docDefinition).download(`Invoice_${selectedInvoice?._id}.pdf`);
  };

  return (
    <>
      <Drawer
        title={`Invoice #${selectedInvoice?._id}`}
        width={400}
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
      >
        {selectedInvoice && (
          <div id="invoice-drawer-content">
            <p><strong>Customer:</strong> {selectedInvoice?.client_id?.name}</p>
            <p><strong>Date:</strong> {selectedInvoice.createdAt ? moment(selectedInvoice?.createdAt).tz("Asia/Kolkata").format("DD-MMM-YYYY") : ""}</p>
            <p><strong>Total Amount:</strong> {selectedInvoice?.amount}</p>
            <p><strong>OrderId:</strong> {selectedInvoice?.orderId}</p>
            <Button icon={<PrinterOutlined />} onClick={handlePrint} style={{ marginRight: 10 }}>
              Print
            </Button>
            <Button icon={<DownloadOutlined />} onClick={generateInvoicePDF}>
              Download
            </Button>
          </div>
        )}
      </Drawer>

      {/* 
      <Button
        style={{ marginBottom: '20px' }}
        onClick={() => {
          setTableData((prev) => ({ ...prev, loading: true }));
          setFilters({
            name: '',
            createdAt: [],
            deliveryStatus: '',
            phone: '',
            order_type: '',
            orderId: '',
            invoiceNumber: '' 
          });

          setTimeout(() => {
            setPagination({ current: 1, pageSize: 10 });
            setTableKey(tableKey + 1);
          }, 1000);
        }}>Clear Filters</Button> */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <Button
          onClick={() => {
            setTableData((prev) => ({ ...prev, loading: true }));

            // ✅ All filters reset
            setFilters({
              name: '',
              createdAt: [],
              deliveryStatus: '',
              phone: '',
              order_type: '',
              orderId: '',
              invoiceNumber: '',
            });

            // ✅ Invoice filter bhi clear karo (separately)
            setInvoiceFilter('');

            // ✅ Agar tum alag state me data store kar rahe ho
            setAllData([]);

            setTimeout(() => {
              setPagination({ current: 1, pageSize: 10 });
              setTableKey((prev) => prev + 1);
            }, 1000);
          }}
        >
          Clear Filters
        </Button>

        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={generateExcelExport}
          style={{ background: 'linear-gradient(135deg, #1890ff, #69c0ff)', border: 'none' }}
        >
          Export Excel
        </Button>
      </div>
      <BaseTable
        key={tableKey}
        columns={columns}
        dataSource={filteredData}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: invoiceFilter ? filteredData.length : tableData.total,
        }}
        loading={tableData.loading || isFetchingAll}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        bordered
        rowClassName={(record) =>
          record.order_type === 'A La Carte' ? 'row-highlight-red' : ''
        }
      />
    </>
  );
};
